import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Sparkles, 
  Send,
  Printer,
  Trash2,
  User,
  Bot,
  Loader2,
  Plus,
  MessageSquare,
  GitBranch,
  HelpCircle,
  RefreshCw,
  FileText,
  Radio,
  Pencil,
  Check,
  X
} from "lucide-react";
import { formatAIResponse } from "@/utils/formatAIContent";

// QI Templates
import { FishboneTemplate } from "@/components/qi/FishboneTemplate";
import { FiveWhysTemplate } from "@/components/qi/FiveWhysTemplate";
import { PDSATemplate } from "@/components/qi/PDSATemplate";
import { A3Template } from "@/components/qi/A3Template";
import { SBARTemplate } from "@/components/qi/SBARTemplate";

const QI_TOOLS = [
  { id: "chat", label: "Open Chat", icon: MessageSquare, description: "Free-form AI discussion" },
  { id: "fishbone", label: "Fishbone", icon: GitBranch, description: "Ishikawa root cause analysis" },
  { id: "5whys", label: "5 Whys", icon: HelpCircle, description: "Deep-dive root cause" },
  { id: "pdsa", label: "PDSA", icon: RefreshCw, description: "Rapid cycle improvement" },
  { id: "a3", label: "A3", icon: FileText, description: "One-page problem solving" },
  { id: "sbar", label: "SBAR", icon: Radio, description: "Structured communication" },
];

export default function AIAssistant() {
  const [activeTab, setActiveTab] = useState("chat");
  const [conversations, setConversations] = useState([]);
  const [currentConversation, setCurrentConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputContext, setInputContext] = useState("");
  const [inputProblem, setInputProblem] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [editingConvoId, setEditingConvoId] = useState(null);
  const [editingTitle, setEditingTitle] = useState("");
  const messagesEndRef = useRef(null);

  useEffect(() => {
    loadConversations();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Load conversations from backend
  const loadConversations = async () => {
    try {
      const response = await axios.get(`${API}/ai/conversations`, { withCredentials: true });
      setConversations(response.data || []);
    } catch (error) {
      console.error("Error loading conversations:", error);
      // Fallback to sessionStorage
      const saved = sessionStorage.getItem("ai_conversations");
      if (saved) {
        setConversations(JSON.parse(saved));
      }
    }
  };

  const startNewConversation = () => {
    setCurrentConversation(null);
    setMessages([]);
    setInputContext("");
    setInputProblem("");
  };

  const selectConversation = async (convo) => {
    try {
      // Load full conversation from backend
      const response = await axios.get(`${API}/ai/conversations/${convo.conversation_id}`, { withCredentials: true });
      const fullConvo = response.data;
      setCurrentConversation(fullConvo);
      setMessages(fullConvo.messages || []);
    } catch (error) {
      console.error("Error loading conversation:", error);
      // Fallback to local data
      setCurrentConversation(convo);
      setMessages(convo.messages || []);
    }
  };

  const deleteConversation = async (conversationId) => {
    try {
      await axios.delete(`${API}/ai/conversations/${conversationId}`, { withCredentials: true });
      setConversations(prev => prev.filter(c => c.conversation_id !== conversationId));
      if (currentConversation?.conversation_id === conversationId) {
        setCurrentConversation(null);
        setMessages([]);
      }
      toast.success("Conversation deleted");
    } catch (error) {
      toast.error("Failed to delete conversation");
    }
  };

  const startRenaming = (convo, e) => {
    e.stopPropagation();
    setEditingConvoId(convo.conversation_id);
    setEditingTitle(convo.title);
  };

  const cancelRenaming = (e) => {
    e?.stopPropagation();
    setEditingConvoId(null);
    setEditingTitle("");
  };

  const saveRename = async (conversationId, e) => {
    e?.stopPropagation();
    if (!editingTitle.trim()) {
      toast.error("Title cannot be empty");
      return;
    }
    
    try {
      await axios.patch(
        `${API}/ai/conversations/${conversationId}`,
        { title: editingTitle.trim() },
        { withCredentials: true }
      );
      
      // Update local state
      setConversations(prev => prev.map(c => 
        c.conversation_id === conversationId 
          ? { ...c, title: editingTitle.trim() }
          : c
      ));
      
      // Update current conversation if it's the one being renamed
      if (currentConversation?.conversation_id === conversationId) {
        setCurrentConversation(prev => ({ ...prev, title: editingTitle.trim() }));
      }
      
      setEditingConvoId(null);
      setEditingTitle("");
      toast.success("Conversation renamed");
    } catch (error) {
      console.error("Error renaming conversation:", error);
      toast.error("Failed to rename conversation");
    }
  };

  // Handler for QI templates to add AI responses to chat
  const handleAIAssist = async (aiResponse) => {
    const aiMessage = {
      id: `msg_${Date.now()}_ai`,
      role: "assistant",
      content: aiResponse,
      timestamp: new Date().toISOString(),
      source: activeTab // Track which tool generated this
    };

    const updatedMessages = [...messages, aiMessage];
    setMessages(updatedMessages);

    // Note: QI tool responses are not persisted to DB - they're just shown in the chat
    // Switch to chat view to see the response
    setActiveTab("chat");
  };

  const sendMessage = async () => {
    if (!inputProblem.trim()) {
      toast.error("Please describe what you need help with");
      return;
    }

    const userMessage = {
      id: `msg_${Date.now()}`,
      role: "user",
      context: inputContext.trim(),
      content: inputProblem.trim(),
      timestamp: new Date().toISOString()
    };

    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    setIsLoading(true);

    try {
      // Build conversation history for context
      const conversationHistory = messages.map(m => ({
        role: m.role,
        content: m.content
      }));

      // Use the smart chat endpoint with conversation_id for persistence
      const response = await axios.post(`${API}/ai/chat`, {
        message: inputProblem.trim(),
        context: inputContext.trim() || null,
        conversation_history: conversationHistory,
        conversation_id: currentConversation?.conversation_id || null
      }, { withCredentials: true });

      const aiMessage = {
        id: `msg_${Date.now()}_ai`,
        role: "assistant",
        content: response.data.response,
        suggested_tool: response.data.suggested_tool,
        tool_reason: response.data.tool_reason,
        timestamp: new Date().toISOString()
      };

      const finalMessages = [...updatedMessages, aiMessage];
      setMessages(finalMessages);

      // Update conversation with returned conversation_id
      const conversationId = response.data.conversation_id;
      const title = messages.length === 0 
        ? inputProblem.slice(0, 50) + (inputProblem.length > 50 ? "..." : "")
        : currentConversation?.title || "Discussion";
      
      const updatedConvo = {
        conversation_id: conversationId,
        title: title,
        messages: finalMessages,
        created_at: currentConversation?.created_at || new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      // Update local state
      setCurrentConversation(updatedConvo);
      
      // Update conversations list
      const existingIndex = conversations.findIndex(c => c.conversation_id === conversationId);
      if (existingIndex >= 0) {
        setConversations(prev => {
          const updated = [...prev];
          updated[existingIndex] = updatedConvo;
          return updated;
        });
      } else {
        setConversations(prev => [updatedConvo, ...prev]);
      }
      
      setInputProblem("");
      
      // Show tool suggestion toast if applicable
      if (response.data.suggested_tool) {
        const toolNames = {
          '5whys': '5 Whys',
          'fishbone': 'Fishbone Diagram',
          'pdsa': 'PDSA Cycle',
          'a3': 'A3 Report',
          'sbar': 'SBAR'
        };
        toast.info(`💡 Suggested tool: ${toolNames[response.data.suggested_tool] || response.data.suggested_tool}`, {
          description: response.data.tool_reason || "Click the tool tab above to use it",
          duration: 8000
        });
      }
      
    } catch (error) {
      console.error("AI error:", error);
      toast.error("Failed to get AI response. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const printConversation = () => window.print();

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric"
    });
  };

  return (
    <div data-testid="ai-assistant-page" className="h-[calc(100vh-8rem)] flex gap-6 animate-fade-in">
      {/* Sidebar - Conversation List */}
      <div className="w-72 flex-shrink-0 no-print">
        <Card className="bento-card h-full flex flex-col">
          <CardHeader className="pb-2 flex-shrink-0">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-violet-400" />
                Discussions
              </CardTitle>
              <Button 
                size="sm" 
                onClick={startNewConversation}
                data-testid="new-conversation-btn"
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="flex-1 overflow-hidden p-2">
            <ScrollArea className="h-full">
              <div className="space-y-1 pr-2">
                {conversations.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    No discussions yet.<br />Start a new one!
                  </p>
                ) : (
                  conversations.map((convo) => (
                    <div
                      key={convo.conversation_id}
                      className={`group p-3 rounded-lg cursor-pointer transition-colors
                        ${currentConversation?.conversation_id === convo.conversation_id 
                          ? "bg-primary/10 border border-primary/30" 
                          : "hover:bg-zinc-800/50"
                        }`}
                      onClick={() => editingConvoId !== convo.conversation_id && selectConversation(convo)}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          {editingConvoId === convo.conversation_id ? (
                            <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                              <Input
                                value={editingTitle}
                                onChange={(e) => setEditingTitle(e.target.value)}
                                className="h-7 text-sm px-2"
                                autoFocus
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') saveRename(convo.conversation_id, e);
                                  if (e.key === 'Escape') cancelRenaming(e);
                                }}
                              />
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6 text-emerald-400"
                                onClick={(e) => saveRename(convo.conversation_id, e)}
                              >
                                <Check className="w-3 h-3" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6"
                                onClick={cancelRenaming}
                              >
                                <X className="w-3 h-3" />
                              </Button>
                            </div>
                          ) : (
                            <>
                              <p className="text-sm font-medium truncate">{convo.title}</p>
                              <p className="text-xs text-muted-foreground">
                                {formatDate(convo.created_at)} • {convo.messages?.length || 0} msgs
                              </p>
                            </>
                          )}
                        </div>
                        {editingConvoId !== convo.conversation_id && (
                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              onClick={(e) => startRenaming(convo, e)}
                              title="Rename"
                            >
                              <Pencil className="w-3 h-3 text-muted-foreground" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteConversation(convo.conversation_id);
                              }}
                              title="Delete"
                            >
                              <Trash2 className="w-3 h-3 text-red-400" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* QI Tool Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
          <div className="flex-shrink-0 mb-4 no-print">
            <TabsList className="bg-zinc-900 p-1 h-auto flex-wrap gap-1">
              {QI_TOOLS.map((tool) => (
                <TabsTrigger 
                  key={tool.id}
                  value={tool.id}
                  className="flex items-center gap-2 px-3 py-2 data-[state=active]:bg-primary/20"
                  data-testid={`tab-${tool.id}`}
                >
                  <tool.icon className="w-4 h-4" />
                  <span className="hidden sm:inline">{tool.label}</span>
                </TabsTrigger>
              ))}
            </TabsList>
          </div>

          {/* Chat Tab */}
          <TabsContent value="chat" className="flex-1 flex flex-col mt-0 data-[state=inactive]:hidden">
            <Card className="bento-card flex-1 flex flex-col overflow-hidden print:shadow-none print:border-gray-300">
              <CardHeader className="pb-2 flex-shrink-0 border-b border-zinc-800 no-print">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <MessageSquare className="w-5 h-5 text-violet-400" />
                    AI Discussion
                  </CardTitle>
                  {messages.length > 0 && (
                    <Button variant="outline" size="sm" onClick={printConversation}>
                      <Printer className="w-4 h-4 mr-2" /> Print
                    </Button>
                  )}
                </div>
              </CardHeader>

              {/* Print Header */}
              <div className="hidden print:block p-4 border-b">
                <h1 className="text-xl font-bold">AI Discussion</h1>
                <p className="text-sm text-gray-600">
                  {currentConversation?.title} • {new Date().toLocaleDateString()}
                </p>
              </div>

              {/* Messages */}
              <CardContent className="flex-1 overflow-hidden p-0">
                <ScrollArea className="h-full">
                  <div className="p-4 space-y-4">
                    {messages.length === 0 ? (
                      <div className="text-center py-12 no-print">
                        <div className="w-16 h-16 rounded-full bg-violet-500/10 flex items-center justify-center mx-auto mb-4">
                          <Sparkles className="w-8 h-8 text-violet-400" />
                        </div>
                        <h3 className="text-lg font-medium mb-2">Start a Discussion</h3>
                        <p className="text-sm text-muted-foreground max-w-md mx-auto mb-4">
                          Chat freely or use the QI tools above for structured analysis.
                        </p>
                        <div className="flex flex-wrap justify-center gap-2">
                          {QI_TOOLS.slice(1).map((tool) => (
                            <Badge 
                              key={tool.id}
                              variant="outline" 
                              className="cursor-pointer hover:bg-primary/10"
                              onClick={() => setActiveTab(tool.id)}
                            >
                              <tool.icon className="w-3 h-3 mr-1" />
                              {tool.label}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    ) : (
                      messages.map((msg) => (
                        <div
                          key={msg.id}
                          className={`flex gap-3 ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                        >
                          {msg.role === "assistant" && (
                            <div className="w-8 h-8 rounded-full bg-violet-500/20 flex items-center justify-center flex-shrink-0">
                              <Bot className="w-4 h-4 text-violet-400" />
                            </div>
                          )}
                          
                          <div className={`max-w-[80%] ${msg.role === "user" ? "order-first" : ""}`}>
                            {msg.context && msg.role === "user" && (
                              <Badge variant="outline" className="mb-1 text-xs">
                                Context: {msg.context}
                              </Badge>
                            )}
                            {msg.source && msg.role === "assistant" && (
                              <Badge variant="outline" className="mb-1 text-xs capitalize">
                                From: {msg.source}
                              </Badge>
                            )}
                            <div
                              className={`p-3 rounded-lg ${
                                msg.role === "user"
                                  ? "bg-primary text-primary-foreground"
                                  : "bg-zinc-800/50 border border-zinc-700"
                              }`}
                            >
                              <p className="text-sm whitespace-pre-wrap">
                                {msg.role === "assistant" ? formatAIResponse(msg.content) : msg.content}
                              </p>
                            </div>
                            {/* Tool Suggestion Badge */}
                            {msg.role === "assistant" && msg.suggested_tool && (
                              <div 
                                className="mt-2 p-2 bg-violet-500/10 border border-violet-500/30 rounded-lg cursor-pointer hover:bg-violet-500/20 transition-colors"
                                onClick={() => {
                                  const toolMap = {
                                    '5whys': 'fiveWhys',
                                    'fishbone': 'fishbone',
                                    'pdsa': 'pdsa',
                                    'a3': 'a3',
                                    'sbar': 'sbar'
                                  };
                                  setActiveTab(toolMap[msg.suggested_tool] || 'chat');
                                }}
                              >
                                <div className="flex items-center gap-2 text-violet-400 text-xs font-medium">
                                  <Sparkles className="w-3 h-3" />
                                  Recommended Tool: {msg.suggested_tool === '5whys' ? '5 Whys' : 
                                    msg.suggested_tool === 'fishbone' ? 'Fishbone Diagram' :
                                    msg.suggested_tool === 'pdsa' ? 'PDSA Cycle' :
                                    msg.suggested_tool === 'a3' ? 'A3 Report' :
                                    msg.suggested_tool === 'sbar' ? 'SBAR' : msg.suggested_tool}
                                </div>
                                {msg.tool_reason && (
                                  <p className="text-xs text-muted-foreground mt-1">{msg.tool_reason}</p>
                                )}
                                <p className="text-xs text-violet-400/70 mt-1">Click to open tool →</p>
                              </div>
                            )}
                            <p className="text-xs text-muted-foreground mt-1 px-1">
                              {formatTime(msg.timestamp)}
                            </p>
                          </div>

                          {msg.role === "user" && (
                            <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                              <User className="w-4 h-4 text-primary" />
                            </div>
                          )}
                        </div>
                      ))
                    )}
                    
                    {isLoading && (
                      <div className="flex gap-3">
                        <div className="w-8 h-8 rounded-full bg-violet-500/20 flex items-center justify-center">
                          <Bot className="w-4 h-4 text-violet-400" />
                        </div>
                        <div className="bg-zinc-800/50 border border-zinc-700 p-3 rounded-lg">
                          <div className="flex items-center gap-2">
                            <Loader2 className="w-4 h-4 animate-spin text-violet-400" />
                            <span className="text-sm text-muted-foreground">Thinking...</span>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    <div ref={messagesEndRef} />
                  </div>
                </ScrollArea>
              </CardContent>

              {/* Input */}
              <div className="flex-shrink-0 p-4 border-t border-zinc-800 no-print">
                <div className="space-y-3">
                  {messages.length === 0 && (
                    <Input
                      value={inputContext}
                      onChange={(e) => setInputContext(e.target.value)}
                      placeholder="Context (optional: e.g., Unit 400 staffing, de-escalation, risk assessment)"
                      className="text-sm"
                      data-testid="ai-context-input"
                    />
                  )}
                  <div className="flex gap-2">
                    <Textarea
                      value={inputProblem}
                      onChange={(e) => setInputProblem(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder={messages.length === 0 
                        ? "Describe your problem or challenge... I'll suggest the best QI tool to use."
                        : "Continue the conversation... ask follow-up questions or provide more details."
                      }
                      className="min-h-[80px] resize-none"
                      data-testid="ai-problem-input"
                    />
                    <Button
                      onClick={sendMessage}
                      disabled={isLoading || !inputProblem.trim()}
                      className="capture-btn h-auto px-4"
                      data-testid="send-message-btn"
                    >
                      {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                    </Button>
                  </div>
                  {messages.length > 0 && (
                    <p className="text-xs text-muted-foreground text-center">
                      💬 Continue the conversation - the AI remembers your previous messages
                    </p>
                  )}
                </div>
              </div>
            </Card>
          </TabsContent>

          {/* Fishbone Tab */}
          <TabsContent value="fishbone" className="flex-1 overflow-auto mt-0">
            <FishboneTemplate onAIAssist={handleAIAssist} />
          </TabsContent>

          {/* 5 Whys Tab */}
          <TabsContent value="5whys" className="flex-1 overflow-auto mt-0">
            <FiveWhysTemplate onAIAssist={handleAIAssist} />
          </TabsContent>

          {/* PDSA Tab */}
          <TabsContent value="pdsa" className="flex-1 overflow-auto mt-0">
            <PDSATemplate onAIAssist={handleAIAssist} />
          </TabsContent>

          {/* A3 Tab */}
          <TabsContent value="a3" className="flex-1 overflow-auto mt-0">
            <A3Template onAIAssist={handleAIAssist} />
          </TabsContent>

          {/* SBAR Tab */}
          <TabsContent value="sbar" className="flex-1 overflow-auto mt-0">
            <SBARTemplate onAIAssist={handleAIAssist} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
